"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Pill, Dna, FlaskConical, Wheat, GraduationCap, ShieldCheck, Flame, ShieldAlert, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
interface IndustryItem {
  id: string;
  name: string;
  tag: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  challenge: string;
  linkPath: string;
}
const industriesList: IndustryItem[] = [{
  id: "pharma",
  name: "Pharmaceutical Manufacturing",
  tag: "GMP & Compliance",
  icon: Pill,
  challenge: "Ensuring compliance with stringent regulatory standards while maintaining product purity throughout manufacturing.",
  linkPath: "/PharmaceuticalManufacturingPage"
}, {
  id: "biopharma",
  name: "Biopharmaceutical",
  tag: "Process Monitoring",
  icon: Dna,
  challenge: "Real-time monitoring of complex bioprocesses to maintain product quality and batch consistency.",
  linkPath: "/BiopharmaceuticalPage"
}, {
  id: "chemical",
  name: "Chemical Manufacturing",
  tag: "Quality Control",
  icon: FlaskConical,
  challenge: "Verifying chemical composition and purity at every stage of production and blending.",
  linkPath: "/ChemicalManufacturingPage"
}, {
  id: "food",
  name: "Food & Ingredients",
  tag: "Safety & Integrity",
  icon: Wheat,
  challenge: "Detecting contaminants and verifying ingredient authenticity to protect consumer safety.",
  linkPath: "/FoodAndIngredientsPage"
}, {
  id: "academic",
  name: "Academic & Research",
  tag: "Research Excellence",
  icon: GraduationCap,
  challenge: "Supporting analytical research and material characterization for scientific discovery.",
  linkPath: "/AcademicAndResearchPage"
}, {
  id: "law-enforcement",
  name: "Law Enforcement & Public Safety",
  tag: "Public Safety",
  icon: ShieldCheck,
  challenge: "Rapid field identification of controlled substances and forensic evidence at crime scenes.",
  linkPath: "/LawEnforcementPublicSafetyPage"
}, {
  id: "fire-hazmat",
  name: "Fire & HazMat",
  tag: "Emergency Response",
  icon: Flame,
  challenge: "On-scene chemical identification and threat assessment for emergency responders facing unknown hazards.",
  linkPath: "/FireHazMatPage"
}, {
  id: "border-security",
  name: "Border Security & Defence",
  tag: "Border Security",
  icon: ShieldAlert,
  challenge: "Detecting illicit materials, explosives, and contraband at border crossing and security checkpoints.",
  linkPath: "/BorderSecurityAndDefencePage"
}];
export default function Industries() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="industriesgrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 font-medium text-xs">
              <Sparkles className="size-3 text-primary" />
              Sectors We Support
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
            Specialized Analytical Support for Regulated Industries
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            From regulated laboratories to high-throughput manufacturing plants across Ireland and the UK, we deliver application-driven analytical instrumentation and lifecycle qualification.
          </p>
        </div>

        {/* 3-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {industriesList.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.4,
            delay: shouldReduceMotion ? 0 : i * 0.06
          }} className="flex flex-col h-full">
                <Card className="flex flex-col justify-between h-full bg-card border-border hover:border-primary/40 shadow-xs hover:shadow-md transition-all duration-300 rounded-xl group overflow-hidden">
                  <CardHeader className="space-y-4 pb-2">
                    <div className="flex items-center justify-between">
                      <div className="size-12 rounded-lg bg-secondary flex items-center justify-center text-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                        <Icon className="size-6" />
                      </div>
                      <Badge variant="outline" className="text-muted-foreground border-border text-xs font-normal">
                        {item.tag}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-foreground group-hover:text-foreground">
                      {item.name}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="pt-2 flex-1">
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {item.challenge}
                    </p>
                  </CardContent>

                  <CardFooter className="pt-4 border-t border-border/60 bg-muted/20">
                    <Button asChild variant="ghost" size="sm" className="w-full justify-between px-0 font-medium text-foreground group/btn hover:bg-transparent hover:text-primary">
                      <Link to={item.linkPath} className="inline-flex items-center w-full justify-between">
                        <span>Explore Industry</span>
                        <span className="size-7 rounded-lg bg-secondary flex items-center justify-center text-foreground group-hover/btn:bg-primary group-hover/btn:text-primary-foreground transition-colors">
                          <ArrowRight className="size-3.5 transition-transform group-hover/btn:translate-x-0.5" />
                        </span>
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}